import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AreteLogo from '../components/AreteLogo';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetting, setResetting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    navigate('/dashboard');
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* Forest photo background — same hope-3.jpg as dashboard */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `url('${process.env.PUBLIC_URL}/hope-3.jpg') center/cover no-repeat`,
          filter: 'brightness(0.38) saturate(1.15)',
        }}
      />
      {/* Warm vignette overlay for depth */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: [
            'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, rgba(15,30,18,0.55) 100%)',
            'linear-gradient(180deg, rgba(20,35,22,0.20) 0%, transparent 40%, rgba(15,28,18,0.30) 100%)',
          ].join(', '),
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <AreteLogo iconSize={52} fontSize="1.7rem" onDark={true} />
          </div>
          <p className="text-sm tracking-widest uppercase mt-2" style={{ color: 'rgba(198,210,190,0.45)' }}>
            Your Health, Integrated
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-3xl shadow-2xl p-8"
          style={{
            background: 'rgba(245,248,242,0.97)',
            border: '1px solid rgba(214,224,207,0.45)',
          }}
        >
          <h2 className="font-bold text-xl mb-1" style={{ color: '#1B3022' }}>Welcome back</h2>
          <p className="text-sm mb-6" style={{ color: 'rgba(47,74,52,0.55)' }}>Sign in to your health dashboard</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#2F4A34' }}>
                Email
              </label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#2F4A34' }}>
                Password
              </label>
              <input
                className="input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm">
                {error}
              </div>
            )}

            {resetSent && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm">
                Password reset link sent! Check your email.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full text-center"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div className="relative flex items-center my-5">
            <div className="flex-1 border-t" style={{ borderColor: 'rgba(214,224,207,0.50)' }} />
            <span className="px-3 text-xs uppercase tracking-wider" style={{ color: 'rgba(47,74,52,0.40)' }}>or</span>
            <div className="flex-1 border-t" style={{ borderColor: 'rgba(214,224,207,0.50)' }} />
          </div>

          <button
            type="button"
            onClick={() => navigate('/demo')}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-colors"
            style={{
              border: '2px solid rgba(111,138,90,0.25)',
              color: '#6F8A5A',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(111,138,90,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            Try Demo — No account needed
          </button>

          <div className="mt-4 text-center">
            <button
              type="button"
              disabled={resetting}
              onClick={async () => {
                if (!email.trim()) {
                  setError('Enter your email above, then click Forgot Password.');
                  return;
                }
                setError('');
                setResetSent(false);
                setResetting(true);
                const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                  redirectTo: `${window.location.origin}/set-password`,
                });
                setResetting(false);
                if (resetErr) {
                  setError(resetErr.message);
                } else {
                  setResetSent(true);
                }
              }}
              className="text-sm font-medium transition-colors"
              style={{ color: '#6F8A5A' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#2F4A34'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#6F8A5A'; }}
            >
              {resetting ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(198,210,190,0.25)' }}>
          Arete Integrated Health · Private & Secure
        </p>
      </div>
    </div>
  );
}
